import { useQuery, useMutation } from '@tanstack/react-query';
import { queryClient, apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { LogOut } from 'lucide-react';

interface AuthMeResponse {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  impersonatingFrom?: {
    id: string;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
  };
}

export function ImpersonationBanner() {
  const { data } = useQuery<AuthMeResponse | null>({
    queryKey: ['/api/auth/me'],
    queryFn: async () => {
      const res = await fetch('/api/auth/me', { credentials: 'include' });
      if (res.status === 401) return null;
      if (!res.ok) return null;
      return res.json();
    },
  });

  const stopMutation = useMutation({
    mutationFn: () => apiRequest('POST', '/api/auth/stop-impersonating'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/auth/me'] });
    },
  });

  const impersonatingFrom = data?.impersonatingFrom;
  if (!impersonatingFrom) return null;

  const currentName =
    data?.firstName || data?.lastName
      ? [data?.firstName, data?.lastName].filter(Boolean).join(' ')
      : data?.email || 'User';
  const adminName =
    impersonatingFrom.firstName || impersonatingFrom.lastName
      ? [impersonatingFrom.firstName, impersonatingFrom.lastName].filter(Boolean).join(' ')
      : impersonatingFrom.email || 'Admin';

  return (
    <div
      className="sticky top-0 z-50 w-full bg-amber-500/95 text-amber-950 py-2 px-4 flex items-center justify-center gap-4 flex-wrap"
      data-testid="impersonation-banner"
    >
      <span className="text-sm font-medium">
        Viewing as <strong>{currentName}</strong>. Acting on behalf of <strong>{adminName}</strong>.
      </span>
      <Button
        size="sm"
        variant="outline"
        className="border-amber-800/50 bg-white/80 hover:bg-white text-amber-900"
        onClick={() => stopMutation.mutate()}
        disabled={stopMutation.isPending}
        data-testid="button-stop-impersonating"
      >
        {stopMutation.isPending ? (
          'Stopping...'
        ) : (
          <>
            <LogOut className="w-3 h-3 mr-1" />
            Stop Impersonating
          </>
        )}
      </Button>
    </div>
  );
}
